import { UserData, IUserData } from "data";
import { IUser } from "interfaces";
import { OptionType, StringOrOptionType } from "types";
import { Secrets } from "utils";

export interface IUserService {
  all(): Promise<IUser[]>;
  asPairValue(): Promise<OptionType[]>;
  asEmailPairValue(): Promise<StringOrOptionType[]>;
  insert(payload: IUser): Promise<IUser>;
  insertAll(payload: IUser[]): Promise<IUser[]>;
  update(payload: IUser): Promise<IUser>;
  findById(id: string): Promise<IUser>;
  nfindById(id: string): Promise<IUser>;
  findByEmail(email: string): Promise<IUser>;
  findByEmailPassword(email: string, password: string): Promise<IUser>;
  findByIdOrEmail(id: string, email: string): Promise<IUser>;
  isEmailInUse(email: string): Promise<boolean>;
  addHiddenPotluck(id: string, potluckId: string): Promise<IUser>;
  removeHiddenPotluck(id: string, potluckId: string): Promise<IUser>;
}

export class UserService implements IUserService {
  userData: IUserData;

  constructor() {
    this.userData = new UserData();
  }

  /**
   * fetch all the users
   * @returns {IUser[]} a list of users
   */

  async all() {
    const { payload: response } = await this.userData.all();
    return response;
  }

  /**
   * fetch all the users as OptionType array
   * @returns {OptionType[]} a list of users
   */

  async asPairValue() {
    const { payload: response } = await this.userData.all();
    return response.map((item: IUser) => ({ value: item._id, label: item.first_name } as OptionType)) as OptionType[];
  }

  /**
   * fetch all the users as StringOrOptionType array
   * @returns {StringOrOptionType[]} a list of users
   */

  async asEmailPairValue() {
    const { payload: response } = await this.userData.all();
    return response.map((item: IUser) => ({
      label: item.email,
      value: item._id,
    }) as StringOrOptionType) as StringOrOptionType[];
  }

  /**
   * Insert a user to the database
   * @param {IUser} payload the user data
   * @returns {IUser} the inserted user
   */

  async insert(payload: IUser) {
    const { payload: response } = await this.userData.insert([payload]);
    return response[0];
  }

  /**
   * Insert multiple users to the database
   * @param {IUser[]} payload the array of user data
   * @returns {IUser[]} the inserted users
   */

  async insertAll(payload: IUser[]) {
    const { payload: response } = await this.userData.insert(payload);
    return response;
  }

  /**
   * update a user in the database
   * @param {IUser} payload the user data to be updated
   * @returns {IUser} the updated user
   */

  async update(payload: IUser) {
    const { payload: response } = await this.userData.update(payload);
    return response;
  }

  /**
   * fetch a user by id
   * @param {string} id the user id
   * @returns {IUser} a user if found
   */

  async findById(id: string) {
    if (id === "") return null;
    const { payload: response } = await this.userData.find({ _id: id }, true);
    return response;
  }

  /**
   * fetch a user by id (not populated)
   * @param {string} id the user id
   * @returns {IUser} a user if found
   */

  async nfindById(id: string) {
    if (id === "") return null;
    const { payload: response } = await this.userData.find({ _id: id }, false);
    return response;
  }

  /**
   * fetch a user by email
   * @param {string} email the user email
   * @returns {IUser} a user if found
   */

  async findByEmail(email: string) {
    if (email === "") return null;
    const { payload } = await this.userData.find({ email }, true);
    return payload;
  }

  /**
   * fetch a user by email and password
   * @param {string} email the user email
   * @param {string} password the user password
   * @returns {IUser} a user if found
   */

  async findByEmailPassword(email: string, password: string) {
    if (email === "") return null;
    const { payload } = await this.userData.find({ email, password: Secrets.hash(password) }, true);
    return payload;
  }

  /**
   * fetch a user by id or email
   * @param {string} id the user id
   * @param {string} email the user email
   * @returns {IUser} a user if found
   */

  async findByIdOrEmail(id: string, email: string) {
    if (id === "" && email === "") return null;
    const { payload } = await this.userData.find({ $or: [{ "_id": id || "" }, { "email": email }] } as any, true);
    return payload;
  }

  /**
   * check if the email exists
   * @param {string} email the user email
   * @returns {boolean} if found
   */

  async isEmailInUse(email: string) {
    if (email === "") return false;
    return !!(await this.findByEmail(email));
  }

  /**
   * update settings hidden potluck (add)
   * @param {string} id the user id
   * @param {string} potluckId the potluck id
   * @returns {IUser} a user
   */

  async addHiddenPotluck(id: string, potluckId: string) {
    const { payload } = await this.userData.addHiddenPotluck(id, potluckId);
    return payload;
  }

  /**
   * update settings hidden potluck (remove)
   * @param {string} id the user id
   * @param {string} potluckId the potluck id
   * @returns {IUser} a user
   */

  async removeHiddenPotluck(id: string, potluckId: string) {
    const { payload } = await this.userData.removeHiddenPotluck(id, potluckId);
    return payload;
  }
}